/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { useState, useEffect, useCallback } from 'preact/hooks';
import {
  getAttributesForm,
  getCustomerAddress,
  removeCustomerAddress,
} from '@/account/api/';
import { CustomerAddressesModel } from '@/account/data/models';
import { checkIsFunction } from '@/account/lib/checkIsFunction';
import { KeysSortOrderProps, useAddressesProps } from '@/account/types';
import { manageAddressDeletion } from '@/account/lib/manageAddressDeletion';
import { defaultAttributePreset } from '@/account/configs/defaultAttributePreset.config';
import { convertToCamelCase } from '@/account/lib/convertCase';
import normalizeGetAddressData from '@/account/lib/normalizeGetAddressData';
import sortingAddressesList from '@/account/lib/sortingAddressesList';

const isEmpty = (obj: any): boolean => {
  if (obj === undefined || obj === null) return true;
  if (typeof obj !== 'object') return false;
  return Object.keys(obj).length === 0 || Object.values(obj).every(isEmpty);
};

export const useAddresses = ({
  selectShipping,
  selectBilling,
  defaultSelectAddressId,
  onAddressData,
  minifiedView,
  routeAddressesPage,
  onSuccess,
}: useAddressesProps) => {
  const [selectedAddressOption, setSelectedAddressOption] =
    useState<string>('');
  const [isModalRendered, setIsModalRendered] = useState<boolean>(false);
  const [isFormRendered, setIsFormRendered] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [addNewAddress, setAddNewAddress] = useState<boolean>(false);
  const [addressId, setEditAddressId] = useState<string>('');
  const [addressesList, setAddressesList] = useState<CustomerAddressesModel[]>(
    []
  );
  const [keysSortOrder, setKeysSortOrder] = useState<KeysSortOrderProps[] | []>(
    []
  );

  const getAddressesList = useCallback(async () => {
    setLoading(true);

    Promise.all([getAttributesForm('shortRequest'), getCustomerAddress()])
      .then((response) => {
        const [attributesFormResponse, addressResponse] = response;

        if (attributesFormResponse) {
          const result: KeysSortOrderProps[] = attributesFormResponse.map(
            ({ name, orderNumber, label }) => ({
              name: convertToCamelCase(name as string),
              orderNumber,
              label: !defaultAttributePreset.includes(name as string)
                ? label
                : null,
            })
          );

          setKeysSortOrder(result);
        }

        if (addressResponse) {
          if (minifiedView) {
            const defaultAddresses = addressResponse.filter(
              (el) => Boolean(el.defaultShipping) || Boolean(el.defaultBilling)
            );

            const addressesToSet = defaultAddresses.length
              ? defaultAddresses
              : addressResponse.slice(-1);

            setAddressesList(addressesToSet);
          } else {
            setAddressesList(addressResponse);
          }
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [minifiedView]);

  useEffect(() => {
    getAddressesList();
  }, [getAddressesList]);

  useEffect(() => {
    if (!addressesList.length) return;

    if (defaultSelectAddressId === 0) {
      setAddNewAddress(true);
      setSelectedAddressOption('0');
    } else {
      const item =
        addressesList.find((el) => {
          if (typeof defaultSelectAddressId === 'string') {
            return (
              el.uid === defaultSelectAddressId ||
              el.id?.toString() === defaultSelectAddressId
            );
          }
          return +el.id! === defaultSelectAddressId;
        }) ||
        sortingAddressesList(addressesList, selectShipping!, selectBilling!)[0];

      const normalizeAddressData = {
        data: normalizeGetAddressData(item),
        isDataValid: !isEmpty(item),
      };

      setSelectedAddressOption(item?.id?.toString() || item?.uid || '');

      onAddressData?.(normalizeAddressData);
    }
  }, [
    addressesList,
    defaultSelectAddressId,
    onAddressData,
    selectBilling,
    selectShipping,
  ]);

  const setEditingAddressId = useCallback((id: string) => {
    setEditAddressId(id);
    setAddNewAddress(false);
  }, []);

  const handleSelectAddressOption = useCallback(
    (event: Event, item?: {} | CustomerAddressesModel | undefined) => {
      const value = (event?.target as HTMLInputElement).value;
      const nextSibling = (event?.target as HTMLInputElement)
        .nextSibling as HTMLElement;

      setSelectedAddressOption(value);

      const normalizeAddressData = {
        data: normalizeGetAddressData(item),
        isDataValid: !isEmpty(normalizeGetAddressData(item)),
      };

      onAddressData?.(normalizeAddressData);
      setAddNewAddress(value === '0');

      if (nextSibling) {
        nextSibling.focus();
        window.scrollBy(0, 100);
      }
    },
    [onAddressData]
  );

  const handleRenderForm = useCallback(() => {
    setIsFormRendered(true);
  }, []);

  const onCloseBtnClick = useCallback(() => {
    setEditAddressId('');
    setIsFormRendered(false);
    setIsModalRendered(false);
  }, []);

  const handleRenderModal = useCallback(() => {
    setIsModalRendered(true);
  }, []);

  const removeAddress = useCallback(async () => {
    setSubmitLoading(true);

    await manageAddressDeletion(addressId, addressesList);

    removeCustomerAddress(+addressId)
      .then(() => {
        getAddressesList();
        onCloseBtnClick();
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  }, [addressesList, addressId, onCloseBtnClick, getAddressesList]);

  const closeNewAddressForm = useCallback(() => {
    setAddNewAddress(false);
  }, []);

  const redirectToAddressesRoute = useCallback(() => {
    const isClientRoute =
      checkIsFunction(routeAddressesPage) && minifiedView && !addNewAddress;

    if (isClientRoute) {
      window.location.href = routeAddressesPage();
    } else {
      setAddNewAddress(true);
      setEditAddressId('');
    }
  }, [addNewAddress, routeAddressesPage, minifiedView]);

  const handleOnSuccess = useCallback(async () => {
    await getAddressesList();
    await onSuccess?.();
  }, [getAddressesList, onSuccess]);

  return {
    keysSortOrder,
    submitLoading,
    isModalRendered,
    isFormRendered,
    loading,
    addNewAddress,
    addressesList,
    addressId,
    handleRenderForm,
    handleRenderModal,
    removeAddress,
    onCloseBtnClick,
    setEditingAddressId,
    closeNewAddressForm,
    redirectToAddressesRoute,
    handleOnSuccess,
    handleSelectAddressOption,
    selectedAddressOption,
  };
};
